import AboutTemplate from "../../components/about/AboutTemplate";
import Page from "../Page";
import { Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import Section from "../../components/about/Section";
import DeveloperNotes from "../../components/about/DeveloperNotes";
import ResponsiveImage from "../../components/about/ResponsiveImage";

const ActivitiesAbout = () => {
  return (
    <Page
      className="about-page activities-about-page"
      pageKey="activities-about"
    >
      <AboutTemplate>
        <Typography variant="h1">Activities</Typography>

        <Section>
          <Typography variant="p1">
            Activities represent the recurring, non-task things you do regularly
            - like exercise, reading, meditation, or hobbies.
          </Typography>
          <Typography variant="p1">
            Unlike tasks, activities aren't something you complete and tick off.
            They're ongoing parts of your routine that you want to make time
            for.
          </Typography>
          <ResponsiveImage
            pageKey="activities"
            imageName="activities-list"
            size="large"
          />
        </Section>

        <Section title="Creating an activity">
          <Typography variant="p1">
            To create an activity, navigate to the Activities page and tap the
            add button. You'll be asked to provide:
          </Typography>
          <Typography variant="p1">
            <strong>Name</strong> - What the activity is, such as "Morning
            Exercise" or "Practice Guitar".
          </Typography>
          <Typography variant="p1">
            <strong>Emoji</strong> - Choose an emoji to represent the activity
            visually.
          </Typography>
          <Typography variant="p1">
            <strong>Colour</strong> - Pick a colour so the activity is easy to
            spot in your day plan.
          </Typography>
          <Typography variant="p1">
            <strong>Description</strong> (optional) - Add a short note about
            what the activity involves.
          </Typography>
          <Typography variant="p1">
            <strong>Duration</strong> (optional) - Set a default duration for
            the activity. When you add the activity to your{" "}
            <Link
              component={RouterLink}
              to="/about/features/day-planner"
              variant="p1Link"
            >
              Day Planner
            </Link>
            , the block's end time will be automatically calculated.
          </Typography>
          <ResponsiveImage
            pageKey="activities"
            imageName="create-activity-dialog"
            size="medium"
          />
        </Section>

        <Section title="Managing activities">
          <Typography variant="p1">
            Your activities are displayed as a list on the Activities page. Each
            activity shows its emoji, name, colour, and description.
          </Typography>
          <Typography variant="p1">
            You can <strong>edit</strong> an activity by tapping on it to update
            its name, emoji, colour, description, or duration.
          </Typography>
          <Typography variant="p1">
            You can <strong>delete</strong> an activity if you no longer need
            it. Deleting an activity will not remove any existing activity
            blocks in your day plans that reference it.
          </Typography>
          <ResponsiveImage
            pageKey="activities"
            imageName="activity-card"
            size="medium"
          />
        </Section>

        <Section title="Using activities in the Day Planner">
          <Typography variant="p1">
            Once you've created your activities, you can schedule them into your
            day using{" "}
            <Link
              component={RouterLink}
              to="/about/features/day-planner/activity-blocks"
              variant="p1Link"
            >
              activity blocks
            </Link>{" "}
            in the Day Planner. Activity blocks pull in the activity's emoji and
            colour, making your day plan colourful and easy to scan.
          </Typography>
          <ResponsiveImage
            pageKey="activities"
            imageName="planner-activity-blocks"
            size="large"
          />
        </Section>

        <Section title="Random activity selector">
          <Typography variant="p1">
            Not sure what to do? The random activity selector spins through your
            activities and picks one for you. It's a fun way to break indecision
            and try something different.
          </Typography>
          <ResponsiveImage
            pageKey="activities"
            imageName="random-activity-selector"
            size="medium"
          />
        </Section>

        <DeveloperNotes>
          <Typography variant="p1">
            I added activities because I realised not everything I want to do in
            a day is a "task". Exercise, reading, and hobbies don't really have
            a start and an end - they're things I want to keep doing regularly.
          </Typography>
          <Typography variant="p1">
            Keeping them separate from tasks means my task stack stays focused
            on actionable things I need to complete, while activities represent
            the ongoing habits I want to maintain.
          </Typography>
          <Typography variant="p1">
            I'd recommend setting a duration on your activities if you can - it
            makes adding them to the Day Planner much quicker since the end time
            is calculated automatically.
          </Typography>
        </DeveloperNotes>
      </AboutTemplate>
    </Page>
  );
};

export default ActivitiesAbout;
